import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  FilterType,
  FILTER_TYPE_TO_READABLE,
  NUMERIC_FILTER_TYPES,
} from 'lookup-config/LookupConfigModalConstants';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {NumericInputChangeEvent} from 'util/Types';

import {FindReplaceTransform} from '../../RunSchemaSpecConstants';
import LookupConfigInput from './LookupConfigInput';
import {
  CSVTextAreaInput,
  FindReplaceFormErrors,
  getColumnNamesFromInput,
  MULTI_VALUE_FILTER_TYPES,
  updateTransformationProperties,
  validateLookupIsNumeric,
} from './TransformFormUtils';

interface FindReplaceTypeSelectorProps {
  value: FindReplaceType;
  opposingValue: FindReplaceType;
  setValue: (value: FindReplaceType) => void;
  transform: FindReplaceTransform;
  updateTransform: (transform: FindReplaceTransform) => void;
  valueKey: 'findValue' | 'replaceValue';
  getOptionName?: (value: FindReplaceType) => string;
}

function FindReplaceTypeSelector(props: FindReplaceTypeSelectorProps): JSX.Element {
  return (
    <SelectDropdown
      alwaysDropDown={true}
      menu={
        <SearchableMenu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          selectedValue={props.value}
          onSelect={(value: FindReplaceType) => {
            props.setValue(value);
            if (value === FindReplaceType.LOOKUP) {
              const newTransformationProperties = {...props.transform.transformationProperties};
              delete newTransformationProperties[props.valueKey];
              props.updateTransform({
                ...props.transform,
                transformationProperties: newTransformationProperties,
              });
            } else if (value === FindReplaceType.VALUE) {
              const newTransform = {...props.transform};
              delete newTransform.lookupConfig;
              newTransform.transformationProperties[props.valueKey] = '';
              props.updateTransform(newTransform);
            }
          }}
          optionDatas={Object.values(FindReplaceType).map((option) => ({
            name: props.getOptionName ? props.getOptionName(option) : option,
            value: option,
            disabled: option === FindReplaceType.LOOKUP ? props.opposingValue === option : false,
            disabledMessage: `${FindReplaceType.LOOKUP} can only be used once`,
          }))}
        />
      }
      displayName={props.value}
    />
  );
}

interface FindReplaceValueInputProps {
  filterType?: FilterType;
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  hasError: boolean;
}

function FindReplaceValueInput(props: FindReplaceValueInputProps): JSX.Element {
  const isNumeric = props.filterType != null && NUMERIC_FILTER_TYPES.includes(props.filterType);
  if (isNumeric) {
    return (
      <>
        <NumericInput
          value={props.value}
          onChange={(e: NumericInputChangeEvent) => {
            if (e.target.numericValue != null) {
              props.onChange(e.target.numericValue);
            }
          }}
        />
        {props.hasError ? <ErrorMessage error="Value is required" /> : null}
      </>
    );
  }
  return (
    <>
      <TextInput
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
      {props.hasError ? <ErrorMessage error="Value is required" /> : null}
    </>
  );
}

export enum FindReplaceType {
  VALUE = 'Value',
  LOOKUP = 'Lookup',
}

function getFindTypeForTransform(transform: FindReplaceTransform): FindReplaceType {
  if (transform.lookupConfig && transform.transformationProperties.findValue == null) {
    return FindReplaceType.LOOKUP;
  }
  return FindReplaceType.VALUE;
}

function getReplaceTypeForTransform(transform: FindReplaceTransform): FindReplaceType {
  if (transform.lookupConfig && transform.transformationProperties.replaceValue == null) {
    return FindReplaceType.LOOKUP;
  }
  return FindReplaceType.VALUE;
}

interface FindReplaceFormProps {
  baseSchema: Schema;
  transform: FindReplaceTransform;
  onChange: (transform: FindReplaceTransform) => void;
  formErrors: FindReplaceFormErrors;
}

export default function FindReplaceForm(props: FindReplaceFormProps): JSX.Element {
  const [findType, setFindType] = useState(getFindTypeForTransform(props.transform));
  const [replaceType, setReplaceType] = useState(getReplaceTypeForTransform(props.transform));
  const filterType = props.transform.transformationProperties.comparisonType;
  const isNumeric = filterType != null && NUMERIC_FILTER_TYPES.includes(filterType);
  const isMulti = filterType != null && MULTI_VALUE_FILTER_TYPES.includes(filterType);
  return (
    <>
      <div className={css(styles.description)}>Find what:</div>
      <Row>
        <Col md={4}>
          <FieldLabel label="Find any value that..." labelStyle={LabelStyle.LABEL} isRequired={true} />
          <SelectDropdown
            alwaysDropDown={true}
            error={props.formErrors.comparisonType ? 'Comparison type is required' : null}
            menu={
              <SearchableMenu
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.FULL}
                selectedValue={filterType}
                onSelect={(value: FilterType) => {
                  const newTransformationProperties = {
                    ...props.transform.transformationProperties,
                    comparisonType: value,
                  };
                  const findValue = props.transform.transformationProperties.findValue;
                  if (NUMERIC_FILTER_TYPES.includes(value) && typeof findValue === 'string') {
                    delete newTransformationProperties.findValue;
                  }
                  if (!NUMERIC_FILTER_TYPES.includes(value) && typeof findValue === 'number') {
                    newTransformationProperties.findValue = findValue.toString();
                  }
                  props.onChange({
                    ...props.transform,
                    transformationProperties: newTransformationProperties,
                  });
                }}
                optionDatas={Object.values(FilterType)
                  .filter((option) => ![FilterType.ISNULL, FilterType.NOTNULL].includes(option))
                  .map((option) => ({
                    name: FILTER_TYPE_TO_READABLE[option],
                    value: option,
                  }))}
              />
            }
            displayName={filterType ? FILTER_TYPE_TO_READABLE[filterType] : 'Select filter type'}
          />
        </Col>
        <Col md={4}>
          <FieldLabel label="Find type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <FindReplaceTypeSelector
            value={findType}
            setValue={setFindType}
            opposingValue={replaceType}
            transform={props.transform}
            updateTransform={props.onChange}
            valueKey="findValue"
            getOptionName={(option: FindReplaceType) =>
              option === FindReplaceType.VALUE ? 'Value or regex' : option
            }
          />
        </Col>
        <Col md={4}>
          <FieldLabel label="Value" labelStyle={LabelStyle.LABEL} isRequired={true} />
          {findType === FindReplaceType.LOOKUP ? (
            <>
              <LookupConfigInput
                baseSchema={props.baseSchema}
                transform={props.transform}
                onChange={props.onChange}
                shouldShowIsMulti={false}
                defaultIsMultiValue={isMulti}
                getLookupConfigValidationError={isNumeric ? validateLookupIsNumeric : undefined}
              />
              {props.formErrors.lookupConfig ? <ErrorMessage error="Lookup is required" /> : null}
            </>
          ) : (
            <FindReplaceValueInput
              filterType={filterType}
              value={props.transform.transformationProperties.findValue}
              onChange={(value: string | number) => {
                props.onChange(updateTransformationProperties(props.transform, 'findValue', value));
              }}
              hasError={props.formErrors.findValue}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FieldLabel label="Within:" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <CSVTextAreaInput
            value={props.transform.transformationProperties.columns}
            placeholder="Sample ID, Concentration"
            parseCSVInputFn={getColumnNamesFromInput}
            onChange={(value: Array<string>) => {
              props.onChange(updateTransformationProperties(props.transform, 'columns', value));
            }}
            hasError={props.formErrors.columns}
          />
          {props.formErrors.columns ? <ErrorMessage error="Columns are required" /> : null}
          <div className={css(styles.description)}>
            Enter a list of columns, separated by comma, in which find and replace should occur.
          </div>
        </Col>
      </Row>
      <div className={css(styles.description)}>Replace with:</div>
      <Row>
        <Col md={4}>
          <FieldLabel label="Replace type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <FindReplaceTypeSelector
            value={replaceType}
            setValue={setReplaceType}
            opposingValue={findType}
            transform={props.transform}
            updateTransform={props.onChange}
            valueKey="replaceValue"
          />
        </Col>
        <Col md={8}>
          <FieldLabel label="Value" labelStyle={LabelStyle.LABEL} isRequired={true} />
          {replaceType === FindReplaceType.LOOKUP ? (
            <>
              <LookupConfigInput
                baseSchema={props.baseSchema}
                transform={props.transform}
                onChange={props.onChange}
                shouldShowIsMulti={false}
                defaultIsMultiValue={false}
              />
              {props.formErrors.lookupConfig ? <ErrorMessage error="Lookup is required" /> : null}
            </>
          ) : (
            <FindReplaceValueInput
              onChange={(value: string) => {
                props.onChange(updateTransformationProperties(props.transform, 'replaceValue', value));
              }}
              value={props.transform.transformationProperties.replaceValue}
              hasError={props.formErrors.replaceValue}
            />
          )}
        </Col>
      </Row>
    </>
  );
}

const styles = StyleSheet.create({
  description: {
    fontSize: 13,
    lineHeight: '16px',
    color: GRAY_DARKER,
    paddingTop: '7px',
    paddingBottom: '16px',
  },
});
